import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NAV_LINKS, siteConfig } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement | null>(null)
    const firstLinkRef = useRef<HTMLAnchorElement | null>(null)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow

        root.style.overflow = 'hidden'
        firstLinkRef.current?.focus({ preventScroll: true })

        const onKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            event.stopPropagation()
            setMenuOpen(false)
            menuButtonRef.current?.focus({ preventScroll: true })
        }

        window.addEventListener('keydown', onKey, true)

        return () => {
            window.removeEventListener('keydown', onKey, true)
            root.style.overflow = previousOverflow
        }
    }, [menuOpen])

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <>
            <header
                className={cn(
                    'pointer-events-none fixed inset-x-0 top-0 z-[60] transition-[background-color,backdrop-filter,border-color] duration-500',
                    scrolled ? 'border-b border-white/[0.06] bg-ink/70 backdrop-blur-md' : 'border-b border-transparent'
                )}
            >
                <div className='flex h-16 items-center justify-between px-[var(--site-gutter)] md:h-[72px]'>
                    <Link
                        href='/'
                        className='pointer-events-auto font-heading text-[13px] font-semibold uppercase tracking-[0.16em] text-bone transition-opacity hover:opacity-80 md:text-[14px]'
                        aria-label={`${siteConfig.name}, home`}
                    >
                        Geo Smyrniotis
                    </Link>

                    <nav aria-label='Main' className='pointer-events-auto hidden items-center gap-10 md:flex'>
                        {NAV_LINKS.map(link => {
                            const active = isActive(link.href)

                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'group relative flex items-center gap-2.5 text-[11px] font-medium uppercase tracking-[0.32em] transition-colors duration-300',
                                        active ? 'text-white' : 'text-white/65 hover:text-white'
                                    )}
                                >
                                    <span
                                        aria-hidden
                                        className={cn(
                                            'block rounded-full transition-all duration-300',
                                            active ? 'size-1.5 bg-ivory' : 'size-1 bg-white/0 group-hover:bg-white/40'
                                        )}
                                    />
                                    {link.label}
                                </Link>
                            )
                        })}
                    </nav>

                    <button
                        ref={menuButtonRef}
                        type='button'
                        onClick={() => setMenuOpen(true)}
                        aria-label='Open menu'
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        className='pointer-events-auto flex size-11 items-center justify-center rounded-full border border-white/25 bg-white/[0.04] text-white backdrop-blur-md md:hidden'
                    >
                        <Menu className='size-5' strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <div
                id='mobile-menu'
                role='dialog'
                aria-modal='true'
                aria-label='Menu'
                aria-hidden={!menuOpen}
                inert={!menuOpen}
                className={cn(
                    'fixed inset-0 z-[90] flex flex-col bg-ink/[0.97] backdrop-blur-xl transition-opacity duration-500 md:hidden',
                    menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
            >
                <div aria-hidden className='film-grain pointer-events-none absolute inset-0 opacity-[0.05]' />

                <div className='relative flex h-16 items-center justify-between px-[var(--site-gutter)]'>
                    <span className='font-heading text-[13px] font-semibold uppercase tracking-[0.16em] text-bone'>
                        Geo Smyrniotis
                    </span>

                    <button
                        type='button'
                        onClick={() => {
                            setMenuOpen(false)
                            menuButtonRef.current?.focus({ preventScroll: true })
                        }}
                        aria-label='Close menu'
                        className='flex size-11 items-center justify-center rounded-full border border-white/25 bg-white/[0.04] text-white'
                    >
                        <X className='size-5' strokeWidth={1.5} />
                    </button>
                </div>

                <nav aria-label='Mobile' className='relative flex flex-1 flex-col justify-center gap-2 px-[var(--site-gutter)]'>
                    <Link
                        href='/'
                        className='group flex items-baseline gap-5 py-2'
                        aria-current={router.pathname === '/' ? 'page' : undefined}
                    >
                        <span className='font-heading text-sm italic text-white/40'>00</span>
                        <span className='font-heading text-[clamp(2.6rem,13vw,4.5rem)] font-semibold uppercase leading-none tracking-[-0.02em] text-bone'>
                            Home
                        </span>
                    </Link>

                    {NAV_LINKS.map((link, index) => (
                        <Link
                            key={link.href}
                            ref={index === 0 ? firstLinkRef : undefined}
                            href={link.href}
                            className='group flex items-baseline gap-5 py-2'
                            aria-current={isActive(link.href) ? 'page' : undefined}
                        >
                            <span className='font-heading text-sm italic text-white/40'>
                                {String(index + 1).padStart(2, '0')}
                            </span>
                            <span className={cn(
                                'font-heading text-[clamp(2.6rem,13vw,4.5rem)] font-semibold uppercase leading-none tracking-[-0.02em]',
                                isActive(link.href) ? 'text-ivory' : 'text-bone'
                            )}>
                                {link.label}
                            </span>
                        </Link>
                    ))}
                </nav>

                <div className='relative flex flex-col gap-4 px-[var(--site-gutter)] pb-10 text-[10.5px] font-medium uppercase tracking-[0.3em] text-white/55'>
                    <a href={`mailto:${siteConfig.email}`} className='font-heading text-base normal-case italic tracking-normal text-bone'>
                        {siteConfig.email}
                    </a>

                    <div className='flex gap-6'>
                        <a href={siteConfig.links.instagram} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1.5'>
                            Instagram
                            <ArrowUpRight className='size-3.5' strokeWidth={1.5} />
                        </a>
                        <a href={siteConfig.links.vimeo} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1.5'>
                            Vimeo
                            <ArrowUpRight className='size-3.5' strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SiteHeader
